import React, { memo } from 'react';
// mui
import { Grid, Link, Typography } from '@mui/material';
// lodash
import { isEqual } from 'lodash';
// components
import {
  trackCallStatisticsExplanationViewed,
  trackCallStatisticsGraphFilterChanged,
} from 'src/analytics';
import { LocationCallStatistics } from 'src/hooks/core-api/useCallStatistics';
import { DashboardAnalyticsSummary } from 'src/sections/dashboard';
import DashboardSummaryGraph from './DashboardSummaryGraph';

interface DashboardStatisticsProps {
  statistics: LocationCallStatistics;
}

const DashboardStatistics: React.FC<DashboardStatisticsProps> = ({ statistics }) => {
  const { calls_processed, time_saved, questions, summary_graph } = statistics;

  const pluralize = (count: number, singular: string, plural: string) =>
    count === 1 ? singular : plural;

  // Must have a parent wrapping this component with `<Grid>` component
  return (
    <>
      <Grid item xs={12} md={3}>
        <DashboardAnalyticsSummary
          title="Calls Answered by Lovage"
          onTooltipHover={() => trackCallStatisticsExplanationViewed('Calls Answered by Lovage')}
          stat={`${calls_processed} ${pluralize(calls_processed, 'Call', 'Calls')}`}
          icon={'ant-design:phone-filled'}
          description={
            <Typography color="inherit">
              This is the total amount of inbound calls that Lovage has answered for you during the
              selected period of time.
            </Typography>
          }
          color="primary"
        />
      </Grid>

      <Grid item xs={0} md={1} />
      <Grid item xs={12} md={3}>
        <DashboardAnalyticsSummary
          title="Employee Time Saved"
          onTooltipHover={() => trackCallStatisticsExplanationViewed('Employee Time Saved')}
          stat={
            time_saved < 59
              ? `${time_saved.toFixed(0)} ${pluralize(time_saved, 'Minute', 'Minutes')}`
              : `${(time_saved / 60).toFixed(0)} ${pluralize(time_saved / 60, 'hour', 'hours')}`
          }
          icon={'ant-design:clock-circle-filled'}
          color="info"
          description={
            <Typography color="inherit">
              This is the estimated amount of time that Lovage has saved employees at your location.
              You can find more information about how we calculate this number{' '}
              <Link
                href="https://www.dialogtech.com/wp-content/uploads/2013/08/WP_CallDistributor_Benchmarking_Analysis.pdf"
                target={'_blank'}
                rel="noopener noreferrer"
              >
                here.
              </Link>
            </Typography>
          }
        />
      </Grid>

      <Grid item xs={0} md={1} />

      <Grid item xs={12} md={3}>
        <DashboardAnalyticsSummary
          title="Customer Questions Answered"
          onTooltipHover={() => trackCallStatisticsExplanationViewed('Customer Questions Answered')}
          stat={`${questions} ${pluralize(questions, 'Question', 'Questions')}`}
          icon={'ant-design:question-circle-filled'}
          color="error"
          description={
            <Typography color="inherit">
              This number is the total number of questions that Lovage has answered for your
              clients. Some categories of the questions answered include: questions regarding hours
              of operation, your location, pricing, walk-in policy and more.
            </Typography>
          }
        />
      </Grid>

      <Grid item xs={12} md={3}>
        {/* <DashboardAnalyticsSummary
          title="Booking Request to Link Clicked"
          onTooltipHover={() =>
            trackCallStatisticsExplanationViewed('Booking Request to Link Clicked')
          }
          stat={`${text_conversion.toFixed(0)}% Clicked`}
          icon={'ant-design:calculator-filled'}
          color="success"
          description={
            <Typography color="inherit">
              This is the percentage of clients who called and specifically requested to book an
              appointment, received a text and then subsequently clicked the link in the text
              directing them to your booking website.
            </Typography>
          }
        /> */}
      </Grid>

      <Grid item xs={0} md={1} />

      <Grid item xs={12} md={10} sx={{ mt: 2 }}>
        <DashboardSummaryGraph
          data={summary_graph}
          onFilterChange={(newFilter) => trackCallStatisticsGraphFilterChanged(newFilter)}
        />
      </Grid>
    </>
  );
};

export default memo(DashboardStatistics, isEqual);
